<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'
import Message from 'primevue/message'
import CatalogForm from '@/views/catalog/CatalogForm.vue'
import { useAuthStore } from '@/stores/auth'
import { productApi, setApi, type CatalogFormPayload, type CatalogKind } from '@/services/catalog'

/**
 * Alta de un producto o un set.
 *
 * El tipo **no se elige en el formulario**: lo dice la ruta de la que se viene.
 * `/catalog/sets/create` es un alta de set y `/catalog/products/create` una de
 * producto, así que el formulario solo muestra el campo de categoría cuando
 * corresponde y el usuario no puede crear un set desde la pantalla de productos
 * por accidente.
 *
 * El endpoint lo decide el tipo resuelto: `type` solo existe en sets, así que un
 * endpoint único obligaría a mandar un campo que el backend descartaría para la
 * mitad de los casos.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

/** `products` o `sets`, del `meta` de la ruta. */
const kindPlural = computed(() => (route.meta.catalogKind === 'sets' ? 'sets' : 'products'))

const kind = computed<CatalogKind>(() => (kindPlural.value === 'sets' ? 'set' : 'product'))
const loading = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const generalError = ref<string | null>(null)

/** Sin `catalog.manage` no se puede escribir. El backend también lo exigiría. */
const allowed = computed(() => authStore.hasPermission('catalog.manage'))

/** Campos del 422, tal y como los devuelve el envelope de errores del proyecto. */
function extractServerErrors(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as {
    validationErrors?: Record<string, string[]>
    response?: { data?: { error?: { details?: { fields?: Record<string, string[]> }; message?: string } } }
  }
  return maybeAxios.validationErrors ?? maybeAxios.response?.data?.error?.details?.fields ?? {}
}

function extractMessage(cause: unknown): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? 'No se pudo guardar el registro. Inténtalo de nuevo.'
}

async function handleSubmit(payload: CatalogFormPayload): Promise<void> {
  loading.value = true
  serverErrors.value = {}
  generalError.value = null

  try {
    // `kind` y `type` son discriminantes del cliente: `kind` decide el
    // endpoint y `type` solo existe en sets. Mandarlos en el cuerpo haría que
    // el backend recibiera campos que no están en su contrato.
    const { kind, type, ...fields } = payload

    if (kind === 'set') {
      await setApi.create({ ...fields, type: type ?? 'nacional' })
    } else {
      await productApi.create(fields)
    }

    await router.push({ name: `catalog.${kindPlural.value}.index` })
  } catch (cause: unknown) {
    serverErrors.value = extractServerErrors(cause)
    // Un 422 ya se explica campo a campo; volver a mostrar un mensaje global
    // sería redundante y taparía los errores concretos.
    generalError.value = Object.keys(serverErrors.value).length > 0 ? null : extractMessage(cause)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 class="text-2xl font-semibold text-surface-900 dark:text-surface-100">
          {{ kind === 'set' ? 'Nuevo set' : 'Nuevo producto' }}
        </h1>
        <p class="mt-1 text-surface-500 dark:text-surface-400">
          Los precios se guardan con el valor que introduzcas, sin conversión de moneda
        </p>
      </div>
      <Button
        label="Volver"
        icon="pi pi-arrow-left"
        severity="secondary"
        @click="router.push({ name: `catalog.${kindPlural}.index` })"
      />
    </header>

    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      No tienes permiso de gestión del catálogo, así que no puedes crear registros.
    </Message>

    <Message
      v-if="generalError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ generalError }}
    </Message>

    <CatalogForm
      v-if="allowed"
      :kind="kind"
      submit-label="Crear"
      :loading="loading"
      :server-errors="serverErrors"
      @submit="handleSubmit"
      @cancel="router.push({ name: `catalog.${kindPlural}.index` })"
    />
  </div>
</template>
